import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminListPackages } from '../../api/packagesApi';
import { ADMIN_PATHS, adminPath, adminCompanyPath } from '../../constants/adminPaths';
import { PageSkeleton } from '../../../components/loading/skeletons';
import CustomPlanFormFields from '../components/CustomPlanFormFields';
import {
  formDataToTerms,
  planToFormData,
} from '../constants/customPlanFormConstants';
import {
  cancelCustomPlanInvite,
  getCustomPlan,
  resendCustomPlanInvite,
  sendCustomPlanInvite,
  updateCustomPlan,
} from '../services/customPlanApi';
import '../components/custom-plan-form.css';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export default function CustomPlanDetailPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { admin } = useAdminAuth();

  const [plan, setPlan] = useState(null);
  const [formData, setFormData] = useState(null);
  const [allPackages, setAllPackages] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const acceptBaseUrl = `${window.location.origin}/main/custom-plan/accept?token=`;

  const loadPlan = async () => {
    const data = await getCustomPlan({ admin, planId });
    setPlan(data.plan);
    setFormData(planToFormData(data.plan));
    setInviteEmail(data.plan?.invite?.email || '');
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        setAllPackages(await adminListPackages());
        await loadPlan();
      } catch (err) {
        setError(err?.message || 'Failed to load plan');
      } finally {
        setLoading(false);
      }
    };
    if (admin?.uid && planId) {
      load();
    }
  }, [admin?.uid, planId]);

  const isDraft = plan?.status === 'draft';
  const isInvited = plan?.status === 'invited';
  const isAccepted = plan?.status === 'accepted';
  const readOnly = !isDraft;

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const terms = formDataToTerms(formData);
      await updateCustomPlan({
        admin,
        planId,
        customerLabel: formData.customerLabel,
        terms,
      });
      setSuccess('Plan updated.');
      await loadPlan();
    } catch (err) {
      setError(err?.message || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async () => {
    const email = String(inviteEmail || '').trim();
    if (!isValidEmail(email)) {
      setError('Enter a valid recipient email.');
      return;
    }
    try {
      setActionLoading('send');
      setError('');
      setSuccess('');
      const result = await sendCustomPlanInvite({ admin, planId, email });
      setSuccess('Invitation sent.');
      if (result.plan) {
        setPlan(result.plan);
        setFormData(planToFormData(result.plan));
      } else {
        await loadPlan();
      }
    } catch (err) {
      setError(err?.message || 'Failed to send invite');
    } finally {
      setActionLoading('');
    }
  };

  const handleResend = async () => {
    try {
      setActionLoading('resend');
      setError('');
      setSuccess('');
      await resendCustomPlanInvite({ admin, planId });
      setSuccess('Invitation resent.');
    } catch (err) {
      setError(err?.message || 'Failed to resend invite');
    } finally {
      setActionLoading('');
    }
  };

  const handleCancelInvite = async () => {
    if (!window.confirm('Cancel this invite and return the plan to draft so you can edit terms?')) {
      return;
    }
    try {
      setActionLoading('cancel');
      setError('');
      setSuccess('');
      await cancelCustomPlanInvite({ admin, planId, revertToDraft: true });
      setSuccess('Invite cancelled. Plan is back in draft.');
      await loadPlan();
    } catch (err) {
      setError(err?.message || 'Failed to cancel invite');
    } finally {
      setActionLoading('');
    }
  };

  const copyAcceptUrl = async () => {
    const token = plan?.invite?.token;
    if (!token) return;
    try {
      await navigator.clipboard.writeText(`${acceptBaseUrl}${token}`);
      setSuccess('Invite link copied.');
    } catch {
      setError('Could not copy link.');
    }
  };

  if (loading || !formData) {
    return (
      <div className="admin-page">
        <PageSkeleton variant="form" label="Loading custom plan" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <Link to={adminPath(ADMIN_PATHS.CUSTOM_PLANS)} style={{ fontSize: 13 }}>← Custom plans</Link>
        <h1 className="admin-page__title" style={{ fontSize: '22px', fontWeight: 750, marginTop: 8 }}>
          {plan.customerLabel}
        </h1>
        <p style={{ margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`cp-status cp-status--${plan.status}`}>{plan.status}</span>
          {plan.terms?.planName && <span style={{ color: '#64748b', fontSize: 13 }}>{plan.terms.planName}</span>}
        </p>
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}
      {success && <div className="admin-alert admin-alert--success">{success}</div>}

      <div className="cp-detail-card">
        <CustomPlanFormFields
          formData={formData}
          setFormData={setFormData}
          allPackages={allPackages}
          readOnly={readOnly}
        />

        {isDraft && (
          <div className="cp-detail-actions">
            <button type="button" className="admin-btn admin-btn--primary" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {isDraft && (
        <div className="cp-detail-card cp-invite-box">
          <h3 style={{ margin: '0 0 8px' }}>Step 2 — Send invite</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
            Save your plan first, then email a private link to the customer.
          </p>
          <label>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Recipient email</span>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="customer@company.com"
            />
          </label>
          <div className="cp-detail-actions">
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              disabled={actionLoading === 'send'}
              onClick={handleSendInvite}
            >
              {actionLoading === 'send' ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </div>
      )}

      {isInvited && (
        <div className="cp-detail-card cp-invite-box">
          <h3 style={{ margin: '0 0 8px' }}>Invitation</h3>
          <p style={{ margin: 0, fontSize: 14 }}>Sent to <strong>{plan.invite?.email}</strong></p>
          {plan.invite?.expiresAt && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
              Expires {new Date(plan.invite.expiresAt).toLocaleString()}
            </p>
          )}
          <p className="cp-url">{acceptBaseUrl}{plan.invite?.token}</p>
          <div className="cp-detail-actions">
            <button type="button" className="admin-btn" onClick={copyAcceptUrl}>Copy link</button>
            <button
              type="button"
              className="admin-btn"
              disabled={actionLoading === 'resend'}
              onClick={handleResend}
            >
              {actionLoading === 'resend' ? 'Resending…' : 'Resend email'}
            </button>
            <button
              type="button"
              className="admin-btn"
              disabled={actionLoading === 'cancel'}
              onClick={handleCancelInvite}
            >
              Cancel invite &amp; edit plan
            </button>
          </div>
        </div>
      )}

      {isAccepted && plan.acceptedCompanyId && (
        <div className="cp-detail-card">
          <h3 style={{ margin: '0 0 8px' }}>Accepted</h3>
          <p style={{ margin: 0 }}>
            <Link to={adminCompanyPath(plan.acceptedCompanyId)}>View company</Link>
          </p>
        </div>
      )}
    </div>
  );
}
